import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import Glide from '@glidejs/glide';
import { connect } from 'react-redux';
import api from 'api';
import WP from 'WP';
import debounce from 'lodash.debounce';

import Container from 'partials/Container';
import GridElement from 'partials/GridElement';
import Image from 'partials/Image';
import SmartLink from 'partials/SmartLink';
import FavoriteButton from 'partials/FavoriteButton';

import Arrow from 'icons/ArrowIcon';
import Media, { ScreenWidth } from 'css/Media';

export class Carousel extends Component {

    constructor(props) {
        super(props);
        this.state = {
            active: 0
        };
        this.glideRef = React.createRef();
        this.glide = null;
    }

    componentWillMount() {
        this.checkScreenSize();
    }

    componentDidMount() {
        window.addEventListener('resize', debounce(() => this.checkScreenSize()),500);
        this.getHotels();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.checkScreenSize);

        if(this.glide) {
            this.glide.destroy();
            this.glide = null;
        }
    }

    checkScreenSize() {
        let isDesktop = false;
        let isMobile = false;

        if(ScreenWidth.lessThan('s')) {
            isMobile = true;
        }
        if(ScreenWidth.greaterThan('m')) {
            isDesktop = true;
        }
        this.setState({
            isDesktop: isDesktop,
            isMobile: isMobile
        });
    }

    getHotels() {
       api.accommodationFetcher.getAll().then(() => this.initGlide());
    }

    initGlide() {
        if(this.glideRef.current) {
            this.glide = new Glide('.glide', {
                type: 'slider',
                rewind: false,
                startAt: 0,
                perView: 1,
                peek: this.state.isDesktop ? { before: 0, after: 180 } : this.state.isMobile ? { before: 0, after: 100 } : 0,
                animationDuration: 550
            });

            this.glide.on('run.after', () => this.handleIndex())

            this.glide.mount();
        }
    }

    handlePagination(dir) {
        this.glide.go(dir);
    }

    handleIndex() {
        //set active slide to active index in navigation
        this.setState({active: this.glide._i})
    }

    render() {
        const { active, isMobile } = this.state;
        const { data, accommodation } = this.props;
        
        return(
            accommodation ?
                <Wrapper>
                    <Container grid>
                        <GridElement
                            colStart={1}
                            colEnd={13}
                        >
                            <h2>{data.title}</h2>
                        </GridElement>
                        <NavigationGridElement
                            colStart={15}
                            colEnd={22}
                        >
                            { accommodation.map((hotel, i) => {
                                return (
                                    <StyledSpan key={hotel.id} isActive={active === i}>
                                        {hotel.acf.item_name}
                                    </StyledSpan>
                                )
                            })}
                        </NavigationGridElement>
                    </Container>
                    <Container grid>
                        <PaginationGridElement
                            colStart={3}
                            colEnd={8}
                            colSpan
                        >
                            <ArrowWrapper back onClick={active > 0 ? () => this.handlePagination('<') : null} disabled={active === 0}>
                                <Arrow />
                            </ArrowWrapper>
                            <ArrowWrapper
                                onClick={active < (accommodation.length - 1) ? () => this.handlePagination('>') : null} 
                                disabled={active === (accommodation.length - 1)}
                            >
                                <Arrow />
                            </ArrowWrapper>
                        </PaginationGridElement>
                    </Container>
                    <Container grid bleedRight>
                        <GridElement
                            colStart={3}
                            colEnd={24}
                        >
                            <div className="glide" ref={this.glideRef}>
                                <div className="glide__track" data-glide-el="track">
                                    <div className="glide__slides">
                                        { accommodation.map(hotel => {
                                            return (
                                                <div className="glide__slide" key={hotel.id}>
                                                    <StyledImage image={hotel.acf.featured_image} background ratio={isMobile ? '1:1' : '2:1'} />
                                                    <StyledContent>
                                                        <SmartLink to={hotel.link}>
                                                            <h3>{hotel.acf.hotel_name}</h3>
                                                        </SmartLink>
                                                        { !isMobile && <p>{hotel.acf.description}</p>}
                                                        <FlexBox>
                                                            <FavoriteButton 
                                                                sort={data.sort}
                                                                item={hotel}
                                                            />
                                                        </FlexBox>
                                                    </StyledContent>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </GridElement>
                    </Container>
                </Wrapper>
            : null
        )
    }
}

const mapStateToProps = state => ({
    accommodation: state.accommodation.posts[WP.lang] ? state.accommodation.posts[WP.lang] : [],
});

const actions = {};

export default connect(mapStateToProps, actions)(Carousel);

const Wrapper = styled.section`
    padding-bottom: ${props => props.theme.spacing.blockXL};
    user-select: none;

    div.glide {
        overflow: hidden;
        
        div.glide__track {
            height: auto;

            .glide__slides {
                display: flex;

                .glide__slide {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    color : black;
                    z-index: 1000;

                    a {
                        color: ${props => props.theme.colors.text};
                    }

                    ${Media.greaterThan('s')`
                        flex-direction: row;
                    `}
                }
            }
        }
    }

    .container {
        margin-bottom: ${props => props.theme.spacing.elementM};

        h2 {
            ${props => props.theme.fonts.h1}
        }
    }
`

const NavigationGridElement = styled(GridElement)`
    display: flex;
    justify-content: space-between;
    align-self: center;
`

const StyledSpan = styled.span`
    ${props => props.theme.fonts.p}
    display: block;
    color: ${props => props.isActive ? props.theme.colors.text : props.theme.colors.button};
`

const PaginationGridElement = styled(GridElement)`
    display: flex;
    padding-bottom: ${props => props.theme.spacing.elementS};
`

const ArrowWrapper = styled.div`
    position: relative;
    cursor: pointer;

    svg {
        transition: .1s ease;

        ${props => props.back && css`
            margin-right: ${props => props.theme.spacing.contentL};
            transform: rotate(180deg);
        `}
    }

    ${props => props.disabled && css`
        cursor: auto;
        
        svg {
            fill: ${props => props.theme.colors.input};
        }
    `}

    &:hover {
        ${props => !props.disabled && css`
            &::after {
                position: absolute;
                content: '';
                bottom: 0;
                left: 0;
                width: 27.453px;
                height: 2px;
                background-color: ${props => props.theme.colors.text};
            }
        `}
    }
`

const StyledImage = styled(Image)`
    width: 100%;
    height: 100%;

    ${Media.greaterThan('s')`
        width: 50%;
    `}
`

const StyledContent = styled.div`
    width: 100%;
    padding: ${props => props.theme.spacing.contentXL} ${props => props.theme.spacing.contentXL} 0;

    p {
        padding: ${props => props.theme.spacing.contentL} 0;

        &:last-of-type {
            padding: 0 0 ${props => props.theme.spacing.elementS};
        }
    }

    ${Media.greaterThan('s')`
        width: 50%;
        padding: ${props => props.theme.spacing.contentXL} ${props => props.theme.spacing.contentXL} 0;
    `}

    ${Media.greaterThan('l')`
        padding: ${props => props.theme.spacing.contentXL} ${props => props.theme.spacing.blockL} 0;
    `}
`

const FlexBox = styled.div`
    display: flex;
    align-items: center;

    >a {
        margin-right: ${props => props.theme.spacing.contentXL};
    }
`